import * as React from 'react';

import { 
    Box, 
    List, 
    ListItem, 
    ListItemButton, 
    ListItemText, 
    SxProps,
    Theme
} from '@mui/material';

import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';

import { DragableItemTypes } from '../../Constants';
import { createExperimentSlice } from '../../../../../App/Store/CreateExpriment';
import { CreateExperimentTypes } from '../../../../../App/Store/CreateExpriment';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../App/Store';
import MouseDragTracker from '../../../../Components/Shared/DragMouseTracker';

type GroupStudentsListProps = {
    groupName: string
};

export default function GroupStudentsList({ groupName } : GroupStudentsListProps) : JSX.Element
{
    const dispatch = useDispatch();

    const studentsInGroup = useSelector(
        (state: RootState) => state.CreateExperiment.groupsStep.studentGroups[groupName]
    )
    ?.studentsIds || [];

    const [ { isOver, canDrop }, dropRef ] = useDrop(
        () => ({
            accept: DragableItemTypes.StudentCard,
            canDrop: CanDropValidator,
            drop: OnDrop,
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop()
            }),
        }),
    );

    function CanDropValidator(item: CreateExperimentTypes.Student, monitor: DropTargetMonitor<CreateExperimentTypes.Student, void>) : boolean
    {
        // TODO : implement a selector to check if can be dropped in this group
        return true;
    }

    function OnDrop(student : CreateExperimentTypes.Student, monitor : DropTargetMonitor<unknown, void>)
    {
        dispatch(createExperimentSlice.Actions.addStudentToGroup({studentId: student.id, groupName}));
    }

    const studentsCards : JSX.Element[] = [];

    for(const studentId of studentsInGroup)
    {
        const studentCard = (
            <StudentListItem 
                key = { studentId }
                studentId = { studentId }
            />
        );

        studentsCards.push(studentCard);
    }

    const HoverSx : SxProps<Theme> = (isOver && canDrop) ? {
        border: 'solid 2px rgba(50, 185, 62, 0.7)',
        borderRadius: '.3rem',
        outline: 'solid 2px rgba(0, 128, 2, 0.7)',
        backgroundColor: 'rgba(50, 205, 50, 0.1)'
    } : {
        border: 'solid 2px transparent',
        outline: 'solid 2px transparent'
    };

    return ( 
        <Box
            ref = { dropRef }
            sx = {{
                minHeight: '5rem',
                padding: '.4rem'
            }}
        >
            <Box
                sx = {{
                    minHeight: 'inherit',
                    ...HoverSx
                }}
            >
                <List disablePadding>
                    { studentsCards } 
                </List>
            </Box>     
        </Box> 
    );
}

type StudentListItemProps = {
    studentId: CreateExperimentTypes.StudentId
};

function StudentListItem({ studentId } : StudentListItemProps) : JSX.Element
{
    const student = useSelector((rootState : RootState) => rootState.CreateExperiment.classStep.students[studentId]);

    const [{ isDragging }, drag] = useDrag(() => 
    ({
        type: DragableItemTypes.StudentCard,
        item: student,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    }));

    const item = (
        <ListItem 
            component = "div"
            disablePadding
        >
            <ListItemButton
                ref={ drag }
                sx={{ 
                    py: 0,
                    opacity: isDragging ? 0.5 : 1 
                }}
            >
                <ListItemText
                    primary={`${student.firstName} ${student.lastName}`}
                />
            </ListItemButton>
        </ListItem>
    );

    const onDraggedItem = (
        <MouseDragTracker>
            { item }
        </MouseDragTracker>
    );

    return (
        isDragging ? onDraggedItem : item
    );
}